import { SupplierIcon } from "@/app/Suppliers/Components/SupplierIcon";
import { useWorkOrderRecord } from "@/app/ProDrivers/WorkOrders/Hooks/useWorkOrderRecord";
import { RecordScreen } from "@/lib/Components/Screens/RecordScreen/RecordScreen";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import {
  ChatBubbleLeftRightIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";
import { ActivityIcon, DollarSignIcon } from "lucide-react";
import { useWorkOrderActions } from "@/app/ProDrivers/WorkOrders/Hooks/useWorkOrderActions";
import { Badge } from "@/components/catalyst/badge";
import { Link } from "@/components/catalyst/link";
import { workOrderStatusMap } from "@/app/ProDrivers/WorkOrders/utils/workOrderStatusMap";
import { useTranslations } from "use-intl";
import { BriefcaseIcon } from "@heroicons/react/20/solid";
import { useGlobalSupplier } from "@/app/Suppliers/Utils/useGlobalSupplier";
import { WarningBanner } from "@/lib/Components/Banners/WarningBanner";
import { WorkOrderRecord } from "@/app/ProDrivers/WorkOrders/GraphQL/workOrdersQuery";
import { useDialog } from "@/lib/Components/Dialog/Hooks/useDialog";
import { UpdateWorkOrderNotificationSettingsDialog } from "@/app/ProDrivers/WorkOrders/Components/Dialogs/UpdateWorkOrderNotificationSettingsDialog";
import { WorkOrderAssignmentDrawer } from "@/app/ProDrivers/WorkOrders/Components/Sheets/WorkOrderAssignmentSheet";
import { WorkOrderQuoteDrawer } from "@/app/ProDrivers/WorkOrders/Components/Sheets/WorkOrderQuoteSheet";
import { WorkOrderInvoiceDrawer } from "@/app/ProDrivers/WorkOrders/Components/Sheets/WorkOrderInvoiceSheet";
import {
  ModelType,
  WorkOrderAssignmentStatus,
  WorkOrderStatus,
} from "@/gql/graphql";
import { useThreads } from "@/app/Threads/hooks/useThreads";

export function WorkOrderRecordScreen() {
  "use no memo";
  const t = useTranslations("workOrder");
  const workOrder = useWorkOrderRecord();
  const actions = useWorkOrderActions();
  const { hasSupplier, supplierId } = useGlobalSupplier();
  const { unreadCount: unreadThreadCount } = useThreads({
    supplier_id: supplierId ?? undefined,
    threadable_type: ModelType.WorkOrder,
    threadable_id: workOrder.id,
  });

  const relocation = workOrder.relocation;

  return (
    <>
      <RecordScreen
        tabParams={{ id: workOrder.id }}
        title={workOrder?.reference ?? ""}
        actionsGroups={actions(workOrder)}
        badges={
          <>
            <Badge color={workOrderStatusMap[workOrder.status]}>
              {t(`status.${workOrder.status}`)}
            </Badge>
            {!hasSupplier && workOrder.supplier ? (
              <Badge color="zinc">
                <SupplierIcon
                  model={workOrder.supplier}
                  className="size-3.5 shrink-0 rounded-sm"
                />
                {workOrder.supplier.name}
              </Badge>
            ) : null}
            {relocation ? (
              <Badge color="zinc">
                <Link
                  className="underline"
                  from="/$tenant/$orgId/supplier/{-$supplierId}"
                  to="/$tenant/$orgId/supplier/{-$supplierId}/relocations/$relocationId"
                  params={(prev) => ({
                    ...prev,
                    relocationId: relocation.id,
                  })}
                >
                  {relocation.reference}
                </Link>
              </Badge>
            ) : null}
          </>
        }
        banner={
          <>
            <WorkOrderNotificationWarning workOrder={workOrder} />
            <WorkOrderServiceCenterWarning workOrder={workOrder} />
          </>
        }
        breadcrumbs={
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink
                  from="/$tenant/$orgId/supplier/{-$supplierId}"
                  params={(prev) => prev}
                  to="/$tenant/$orgId/supplier/{-$supplierId}/pro-drivers/work-orders"
                  search={{
                    workOrderStatus: [
                      WorkOrderStatus.QuoteRequested,
                      WorkOrderStatus.QuoteIssued,
                      WorkOrderStatus.AssigningDrivers,
                      WorkOrderStatus.DriversAssigned,
                      WorkOrderStatus.InProgress,
                      WorkOrderStatus.Invoiced,
                    ],
                  }}
                >
                  {t("breadcrumb.proDrivers")}
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>{workOrder.reference}</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        }
        tabs={[
          {
            name: t("tabs.details"),
            Icon: DocumentTextIcon,
            to: "/$tenant/$orgId/supplier/{-$supplierId}/pro-drivers/work-orders/$id",
          },
          {
            name: t("tabs.messages"),
            Icon: ChatBubbleLeftRightIcon,
            to: "/$tenant/$orgId/supplier/{-$supplierId}/pro-drivers/work-orders/$id/messages",
            notificationCount: unreadThreadCount,
            fuzzy: true,
          },
          {
            name: t("tabs.incidents"),
            Icon: ExclamationTriangleIcon,
            to: "/$tenant/$orgId/supplier/{-$supplierId}/pro-drivers/work-orders/$id/incidents",
          },
          {
            name: t("tabs.activity"),
            Icon: ActivityIcon,
            to: "/$tenant/$orgId/supplier/{-$supplierId}/pro-drivers/work-orders/$id/activity",
          },
          {
            name: t("tabs.billing"),
            Icon: DollarSignIcon,
            to: "/$tenant/$orgId/supplier/{-$supplierId}/pro-drivers/work-orders/$id/billing",
            isAdminTab: true,
          },
          {
            name: t("tabs.findDrivers"),
            Icon: BriefcaseIcon,
            to: "/$tenant/$orgId/supplier/{-$supplierId}/pro-drivers/work-orders/$id/job-openings",
            isAdminTab: true,
            notificationCount: workOrder.acceptedOfferCount,
          },
        ]}
      />
      <WorkOrderAssignmentDrawer />
      <WorkOrderQuoteDrawer />
      <WorkOrderInvoiceDrawer />
    </>
  );
}

function WorkOrderNotificationWarning({
  workOrder,
}: {
  workOrder: WorkOrderRecord;
}) {
  const t = useTranslations("workOrder");
  const { open: openNotificationSettings } = useDialog(
    UpdateWorkOrderNotificationSettingsDialog,
  );

  if (workOrder.notificationSettings.length > 0) {
    return null;
  }

  return (
    <div className="mt-6">
      <WarningBanner>
        {t("warnings.noEmailsAttached")}{" "}
        <button
          type="button"
          className="font-medium underline"
          onClick={() => {
            openNotificationSettings({
              workOrderId: workOrder.id,
            });
          }}
        >
          {t("warnings.addAnEmail")}
        </button>
      </WarningBanner>
    </div>
  );
}

function WorkOrderServiceCenterWarning({
  workOrder,
}: {
  workOrder: WorkOrderRecord;
}) {
  const t = useTranslations("workOrder");

  if (!workOrder.is_service_center || !isAwaitingPickup(workOrder)) {
    return null;
  }

  return (
    <div className="mt-6">
      <WarningBanner>{t("warnings.serviceCenterPickup")}</WarningBanner>
    </div>
  );
}

const closedWorkOrderStatuses = [
  WorkOrderStatus.QuoteRejected,
  WorkOrderStatus.Completed,
  WorkOrderStatus.Archived,
];

function isAwaitingPickup(workOrder: WorkOrderRecord) {
  if (closedWorkOrderStatuses.includes(workOrder.status)) {
    return false;
  }

  const pickedUpCount = workOrder.assignments.filter(
    (assignment) => assignment.status !== WorkOrderAssignmentStatus.Assigned,
  ).length;

  return pickedUpCount < workOrder.number_of_drivers;
}
